import { VarsHelper, filterClasses } from "@swivl-mono/helpers";
import {
  AMENITY_URL_KEYS,
  CortexSetting,
  LocationScope,
  LocationVarInheritance,
  LocationVarKey,
  MergedVarsWithKeys,
  URLDef,
  URLKey,
  URLS,
  VarDef,
  VarType
} from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, Input, Tooltip } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { BiEditAlt } from "react-icons/bi";
import { GrRevert } from "react-icons/gr";

import { useMergedLocationVariables } from "../../../../../Hooks/useLocationVariables";
import { usePropertiesGetLocationIds } from "../../../../../Hooks/usePropertiesGetLocationIds";
import { isValidURL } from "../../../../../Models/Variables/LocationVars.Model";
import { CortexInheritancePill } from "../../Components/CortexInheritancePill";
import { revertLabelFor } from "../../Components/revertTarget";
import { TopicProvider, useTopicContext, useTopicHandlers, useTopicRow } from "../../Hooks/useTopicContext";

export const TOPIC_VARS: LocationVarKey[] = URLS.flatMap((u) => [u.urlVarKey, u.enabledVarKey]);

/**
 * Display order within a card, most-used first. The registry's own order is
 * arbitrary — it reflects when each link was added, not how often an
 * operator sets one.
 */
const DISPLAY_ORDER: URLKey[] = [
  URLKey.AccountLogin,
  URLKey.PayOnline,
  URLKey.SizeGuide,
  URLKey.FacilityMap,
  URLKey.PrivacyPolicy,
  URLKey.Career,
  URLKey.Insurance,
  URLKey.Auctions,
  URLKey.MobileApp
];
const AMENITY_DISPLAY_ORDER: URLKey[] = [URLKey.Referral, URLKey.PackingSupplies, URLKey.MovingServices];

/** Orders `urls` by `order`; unlisted ones still render, at the end. */
function orderedBy(order: URLKey[], urls: readonly URLDef[]): URLDef[] {
  return [...order.flatMap((key) => urls.filter((u) => u.key === key)), ...urls.filter((u) => !order.includes(u.key))];
}

const COMMON_URLS = orderedBy(
  DISPLAY_ORDER,
  URLS.filter((u) => !AMENITY_URL_KEYS.includes(u.key))
);
const AMENITY_URLS = orderedBy(
  AMENITY_DISPLAY_ORDER,
  URLS.filter((u) => AMENITY_URL_KEYS.includes(u.key))
);

/** Per-link examples, so the placeholder shows the shape of the page we want. */
const PLACEHOLDERS: Record<URLKey, string> = {
  [URLKey.AccountLogin]: "https://storage.example.com/account/login",
  [URLKey.PayOnline]: "https://storage.example.com/pay",
  [URLKey.SizeGuide]: "https://storage.example.com/unit-size-guide",
  [URLKey.FacilityMap]: "https://storage.example.com/maps/main-street.pdf",
  [URLKey.PrivacyPolicy]: "https://storage.example.com/privacy",
  [URLKey.Career]: "https://storage.example.com/careers",
  [URLKey.GoogleReview]: "https://g.page/r/example/review",
  [URLKey.Referral]: "https://storage.example.com/referral",
  [URLKey.PackingSupplies]: "https://storage.example.com/packing-supplies",
  [URLKey.MovingServices]: "https://storage.example.com/moving-services",
  [URLKey.Insurance]: "https://storage.example.com/insurance",
  [URLKey.Auctions]: "https://storage.example.com/auctions",
  [URLKey.MobileApp]: "https://storage.example.com/app"
};

/**
 * The built-in default for a var, as declared in VarDef — the value a field
 * falls back to when nothing is set at any scope. Same source the server merges
 * from, so the two agree.
 */
function varDefault(varKey: LocationVarKey): string {
  return VarDef[VarsHelper.varKeyForType(VarType.Location, varKey)]?.defaultValue ?? "";
}

/** Matches Input at MEDIUM (`h-[34px]`), so a row's label and tag line up with the field. */
const CONTROL_HEIGHT = "flex h-[34px] items-center";

/**
 * Blank is valid — an empty link is how an operator says the facility doesn't
 * have one, and it's what turns the "bot may share this" flag off. Anything
 * else has to be a full http(s) URL, since the bot hands it to a customer
 * verbatim and a bare domain won't resolve as a link.
 */
function urlError(value: string): string | undefined {
  const trimmed = value.trim();
  if (trimmed === "") return undefined;
  return isValidURL(trimmed) ? undefined : "Enter a full URL starting with http:// or https://";
}

function urlSetting(url: URLDef): CortexSetting {
  return {
    kind: "text",
    key: url.key,
    varKey: url.urlVarKey,
    label: url.label,
    description: url.description,
    placeholder: `e.g. ${PLACEHOLDERS[url.key] ?? "https://..."}`,
    default: varDefault(url.urlVarKey)
  };
}

interface URLGroup {
  id: string;
  title: string;
  description: string;
  urls: URLDef[];
}

const URL_GROUPS: URLGroup[] = [
  {
    id: "common",
    title: "Common URLs",
    description: "Leave a link blank if you don't have one. The bot only shares a link when a customer asks for it.",
    urls: COMMON_URLS
  },
  {
    id: "amenity",
    title: "Amenity URLs",
    description: "Tied to a toggle on Amenities & Services - the bot points customers here once it's on.",
    urls: AMENITY_URLS
  }
];

export function URLsTopic(): React.ReactElement {
  const ctx = useTopicContext();
  const page = usePropertiesGetLocationIds();
  // What a field falls back to once its override here is deleted: whatever the
  // next scope up resolves to. At org scope there's nothing above, so the
  // fallback is the VarDef default instead.
  const isOrgScope = page.type === LocationScope.ORG;
  const parentVars = useMergedLocationVariables(TOPIC_VARS, page.locationId != null ? page.groupId : undefined, undefined);
  // Which card is open — mirrors Amenities & Services.
  const [openGroupId, setOpenGroupId] = useState<string | undefined>(undefined);

  // Save / Cancel clears the worksheet's flag — close the card to match.
  useEffect(() => {
    if (!ctx.isEditing && openGroupId) setOpenGroupId(undefined);
  }, [ctx.isEditing, openGroupId]);

  return (
    <div className="flex flex-col gap-4">
      {URL_GROUPS.map((group) => (
        <URLCard
          key={group.id}
          group={group}
          isEditing={ctx.isEditing && openGroupId === group.id}
          isOrgScope={isOrgScope}
          parentVars={parentVars}
          onEdit={() => {
            setOpenGroupId(group.id);
            ctx.setEditing(true);
          }}
        />
      ))}
    </div>
  );
}

// ─── Card ──────────────────────────────────────────────────────────────────

interface URLCardProps {
  group: URLGroup;
  isEditing: boolean;
  isOrgScope: boolean;
  parentVars: MergedVarsWithKeys<LocationVarKey> | undefined;
  onEdit: () => void;
}

function URLCard({ group, isEditing, isOrgScope, parentVars, onEdit }: URLCardProps): React.ReactElement {
  const ctx = useTopicContext();

  // Blocks Save while any link in this card is malformed.
  const hasInvalidURL = group.urls.some((url) => {
    const staged = ctx.draft[url.urlVarKey]?.value;
    return urlError(staged ?? ctx.merged[url.urlVarKey]?.value ?? "") !== undefined;
  });

  return (
    <section className="overflow-hidden rounded-lg border border-grey-200 bg-white">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <h3 className="heading-base-alt text-grey-900">{group.title}</h3>
          <p className="body-sm mt-0.5 text-grey-600">{group.description}</p>
        </div>
        {isEditing ? (
          <div className="flex shrink-0 items-center gap-2">
            <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} disabled={ctx.saving} onClick={ctx.cancelEdits}>
              Cancel
            </Button>
            <Button color={ButtonColor.ORANGE} size={ButtonSize.SMALL} disabled={ctx.saving || hasInvalidURL} onClickAsync={ctx.saveTopic}>
              {ctx.saving ? "Saving…" : "Save"}
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onEdit}
            className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500"
          >
            Edit
            <BiEditAlt className="size-4" />
          </button>
        )}
      </div>

      <TopicProvider {...ctx} isEditing={isEditing}>
        <div className="border-t border-grey-200">
          {group.urls.map((url) => {
            const setting = urlSetting(url);
            return (
              <URLRow
                key={url.key}
                url={url}
                setting={setting}
                isOrgScope={isOrgScope}
                parentInheritance={parentVars?.[setting.varKey]?.inheritance}
                revertsTo={(isOrgScope ? String(setting.default ?? "") : (parentVars?.[setting.varKey]?.value ?? "")) || "(empty)"}
              />
            );
          })}
        </div>
      </TopicProvider>
    </section>
  );
}

// ─── Row ───────────────────────────────────────────────────────────────────

interface URLRowProps {
  url: URLDef;
  setting: CortexSetting;
  /** Org is the top scope — a revert there lands on the VarDef default. */
  isOrgScope: boolean;
  /** How the next scope up resolves — names the revert's destination. */
  parentInheritance: LocationVarInheritance | undefined;
  /** Display text for the value a revert would restore. */
  revertsTo: string;
}

function URLRow({ url, setting, isOrgScope, parentInheritance, revertsTo }: URLRowProps): React.ReactElement {
  const { merged, draft } = useTopicRow(setting.varKey);
  const handlers = useTopicHandlers(setting.varKey);
  const enabledHandlers = useTopicHandlers(url.enabledVarKey);
  const { isEditing } = useTopicContext();

  const draftValue = draft?.value;
  const overrideStaged = draftValue !== undefined;
  const isOverride = merged.inheritance === LocationVarInheritance.NONE;
  const displayValue = draftValue ?? merged.value ?? String(setting.default ?? "");

  // Typing a URL turns on the "bot may share this" flag; clearing it turns the
  // flag off. There's no separate control — the flag tracks the text.
  const setValue = (value: string): void => {
    handlers.onSettingValue(value);
    enabledHandlers.onSettingValue(value.trim() ? "true" : "false");
  };

  const revertLabel = revertLabelFor({ isOrgScope, isOverride, inheritance: merged.inheritance, parentInheritance });

  const handleRevert = (): void => {
    if (!window.confirm(`${revertLabel}? This will discard the current customization for this field.`)) return;
    // A saved override is deleted server-side, which also clears this row's
    // draft. With nothing saved yet, dropping the draft is the whole job.
    if (isOverride) void handlers.onRevert(merged);
    else handlers.onDiscard();
  };

  // mx rather than px so the row's border stops short of the card edge, lining
  // the divider up with the label and the tag.
  //
  // While editing, the label and the tag column are pinned to the input's own
  // height and the row aligns to the top. A validation message renders inside
  // the Input's container, so plain centring would measure input-plus-error and
  // drop the label below the field it names.
  return (
    <div
      className={filterClasses(
        "mx-4 grid grid-cols-[200px_minmax(0,1fr)_auto] gap-x-4 border-b border-grey-200 py-3 last:border-b-0",
        isEditing ? "items-start" : "items-center"
      )}
    >
      <span className={filterClasses("body-sm-alt text-grey-900", isEditing && CONTROL_HEIGHT)}>{setting.label}</span>

      {/* The control gets a column of its own so the description below can share
          it and match its width exactly, rather than running under the tag. */}
      <div className="min-w-0">
        {!isEditing ? (
          <p className="body-sm break-all text-grey-900">{displayValue || "—"}</p>
        ) : (
          <Input
            value={displayValue}
            error={urlError(displayValue)}
            placeholder={setting.kind === "text" ? setting.placeholder : undefined}
            onChange={(e) => setValue(e.target.value)}
          />
        )}
      </div>

      <div className={filterClasses("flex shrink-0 items-center gap-2", isEditing && CONTROL_HEIGHT)}>
        <CortexInheritancePill inheritance={isOverride || overrideStaged ? LocationVarInheritance.NONE : merged.inheritance} />

        {isEditing && (isOverride || overrideStaged) && (
          <Tooltip content={`Reverts to: ${revertsTo}`}>
            <button
              type="button"
              onClick={handleRevert}
              className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500"
            >
              {revertLabel}
              <GrRevert className="size-4" />
            </button>
          </Tooltip>
        )}
      </div>

      {/* Second grid row, control column — so the description matches the
          control's width and doesn't drag the label off-centre from it. */}
      {isEditing && setting.description && <p className="body-sm col-start-2 mt-1 text-grey-600">{setting.description}</p>}
    </div>
  );
}
